import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar, Box, Grid, Typography } from '@mui/material'
import { format } from 'date-fns'

import { useGET_assignmentByIdQuery } from 'redux/api/assignmentsApi'
import { useGET_conversationMessagesQuery, usePOST_conversationMutation } from 'redux/api/conversationsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import assignmentDetails from 'assets/images/student/bee-tablet.png'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { initialValues, validationSchema } from 'utils/formModels/conversationModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType } from 'types'

import { ELEMENT_RADIUS_MID, PRIMARY_TRANSPARENT_GREEN } from '../../../utils/customizedTheme'
import { Message } from '@mui/icons-material'
import { useMedia } from 'utils/customHooks/useMedia'

interface IProps {
  action: ActionType
  assignmentId?: string
  conversationId?: string
  viewOnly: boolean
  tabText?: string
  tabKey?: string
}

const ConversationForm: FC<IProps> = ({ action, assignmentId, conversationId, viewOnly, tabText }) => {
  const { t } = useTranslation()
  const { isUnder1536px } = useMedia()
  const { text } = formModel()
  const { data: assignment } = useGET_assignmentByIdQuery(assignmentId, { skip: !assignmentId })

  const [id, setId] = useState(conversationId ? conversationId : null)

  useEffect(() => {
    const id = (assignment && assignment?.conversation_id && assignment.conversation_id) ?? null
    if(id)setId(id.toString())
  }, [assignment])


  const [newValue, setNewValue] = useState(null)
  const [
    addEntity,
    { isLoading: createLoading, isSuccess: createSuccess, error: createError, data: addResponseData },
  ] = usePOST_conversationMutation()

  const { isLoading: messagesLoading, data: messages } = useGET_conversationMessagesQuery(id, {
    skip: !id,
  })
  const isLoading = createLoading || messagesLoading
  const error = createError

  useErrorAlert(error)
  useSuccessAlert(createSuccess ? t('alerts.message-sent') : '')

  useEffect(() => {
    if (createSuccess) {
      setNewValue(null)
    }
  }, [createSuccess])

  const data = (messages?.results.length > 0 ? [...messages?.results].reverse() : []) ?? []

  return (
    <Formik
      enableReinitialize
      initialValues={newValue || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: { text: string }) => {
        addEntity({ id, body: { text: values.text } })
      }}
    >
      {({ values, resetForm }) => {
        return (
          <Form
            style={{
              marginTop: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: '1',
                minHeight: '0',
                width: '80%',
              }}
            >
              <Grid item sx={{ 
                  width: '100%', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  flexDirection: isUnder1536px ? 'column' : 'row',
                }} xs={12}>

                <img src={assignmentDetails} style={{ width: '200px', height: '200px', borderRadius: '50%', marginRight: '20px' }} />
                
                <InputField
                  placeholder='Write comment'
                  fieldProps={{ ...text }}
                  disabled={viewOnly}
                  sx={{ 
                    width: '100%',
                    marginRight: isUnder1536px ? 0 : '1.5rem',
                    marginTop: 1,
                    marginBottom: 1
                  }}
                  onChange={(value) => setNewValue({ text: value })}
                />
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('common.comment')}`}
                  icon={<Message />}
                  sx={{ 
                    width: isUnder1536px ? '100%' : '10rem' 
                  }}
                  disabled={!values.text}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
            <Grid item sx={{ width: '100%', marginTop: 3 }} xs={12}>
              {data.length > 0 &&
                data.map((message) => (
                  <Box
                    className="transparent-background"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                    p={2}
                    my={1}
                    key={message.id}
                  >
                    <Grid container sx={{ display: 'flex' }}>
                      <Grid item xs={10} md={2} xl={2} sx={{ textAlign: 'right' }}>
                        <Typography
                           sx={{ fontWeight: '700', fontSize: '0.9rem', '& p': { margin: '0.1rem 0' } }}
                          >
                          {message.author.full_name}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: '700', fontSize: '0.9rem', '& p': { margin: '0.1rem 0' } }}
                        >
                          {message.created_at ? format(new Date(message.created_at), 'yyyy-MM-dd HH:mm') : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} md={2} xl={1}>
                        <Avatar
                          className='avatar'
                          sx={{
                            bgcolor: PRIMARY_TRANSPARENT_GREEN,
                            width: 50,
                            height: 50,
                            marginLeft: '20px',
                            marginRight: '20px',   
                            display: 'inline-block',
                            position: 'relative',
                            lineHeight: '70px',
                            textAlign: 'center'
                          }}
                          variant='circular'
                          src={message.author.avatar}
                        />
                      </Grid>  
                      <Grid item xs={12} md={8} xl={9} sx={{ background: PRIMARY_TRANSPARENT_GREEN, borderRadius: '10px', padding: '10px' }}>
                        <Typography sx={{ fontSize: '1.1rem', '& p': { margin: '0.1rem 0' } }}>
                          {message.text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ConversationForm
